import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { toast } from 'react-toastify';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { AddCircle } from '@mui/icons-material';
import { createEventParticipant } from 'src/shared/services/eventParticipantService';
import { useSchemaAddParticipant } from '../schemas/useSchemaAddParticipant';

type EventAddParticipantModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  successCallback: () => void;
  eventId: number;
  eventFee?: number;
  useTickets: boolean;
};

export const EventAddParticipantModal = (props: EventAddParticipantModalProps) => {
  const { modalOpen, onClose, successCallback, eventId, eventFee, useTickets } = props;
  const { t } = useTranslation();
  const [loading, setIsLoading] = useState(false);
  const [createInvoiceValue, setCreateInvoiceValue] = useState<boolean>(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [hasFee, setHasFee] = useState<boolean>(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const isMounted = useIsMounted();
  // Make a reference to the top portion of the modal content
  // When an error occures, scroll top of modal into view
  const modalTop = useRef<HTMLDivElement>(null);
  const scrollToModalTop = () => modalTop.current && modalTop.current.scrollIntoView();

  const addParticipantRequestSchema = useSchemaAddParticipant();
  type AddParticipantFormInterface = InferType<ReturnType<typeof useSchemaAddParticipant>>;

  const formMethods = useForm<AddParticipantFormInterface>({
    resolver: yupResolver(addParticipantRequestSchema),
    defaultValues: {
      // createInvoice: false,
      participants: [{ firstName: '', lastName: '', email: '', member_id: 0, event_id: eventId, fee: eventFee || 0, allergy: '', phone: '' }],
    },
    mode: 'all',
  });

  const { control, handleSubmit } = formMethods;

  // TODO: Hur typar jag denna?
  const { fields, append, remove } = useFieldArray<any>({
    control,
    name: 'participants',
  });

  const saveFormData = useCallback(
    async (data: AddParticipantFormInterface) => {
      ConsoleHelper.log('data', data);
      setIsLoading(true);
      try {
        // Use Promise.all to post each participant individually
        if (data?.participants) {
          const updatedParticipants = data.participants.map((participant) => ({
            ...participant,
            createInvoice: createInvoiceValue,
            // createInvoice: data.createInvoice,
          }));
          // ConsoleHelper.log('updatedParticipants', updatedParticipants);
          await Promise.all(updatedParticipants.map((participant) => createEventParticipant(participant)));
        }
        if (isMounted()) {
          toast.success(<>{t('common:success.save')}</>);
          successCallback();
        }
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          toast.error(<>{t('common:error.save')}</>);
          setServerErrors(e.data);
          scrollToModalTop();
        }
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    },
    [createInvoiceValue, isMounted, successCallback, t],
  );

  const onSubmit = useCallback(
    (data: AddParticipantFormInterface) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleSave = useCallback(() => {
    if (hasFee && !createInvoiceValue) {
      setConfirmDialogOpen(true);
    } else {
      handleSubmit(onSubmit)();
    }
  }, [createInvoiceValue, handleSubmit, hasFee, onSubmit]);

  // const hasFee = (fields as Array<{ fee?: number }>).some((field) => (field.fee ?? 0) > 0);

  const participants = useWatch({
    control,
    name: 'participants', // watch only the participants field
  });

  useEffect(() => {
    const anyHasFee = participants?.some((participant) => (participant.fee ?? 0) > 0);
    setHasFee(anyHasFee ?? false);
  }, [participants]);

  const handleDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleDialogConfirm = () => {
    setConfirmDialogOpen(false);
    handleSubmit(onSubmit)();
  };

  return (
    <>
      <GenericDialog
        open={modalOpen}
        onClose={() => onClose()}
        onSave={() => ConsoleHelper.log('save')}
        dialogTitle={t('event:addParticipants')}
        padding={0}
        maxWidth='lg'
        dialogContent={
          <Box
            ref={modalTop}
            sx={{
              position: 'relative',
              border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none'),
              minHeight: '20rem',
            }}
          >
            <LoadingOverlay loading={loading} />
            <Box sx={{ p: 2 }}>
              <GridWrapper>
                {useTickets && (
                  <Grid item lg={10}>
                    <Alert severity='info' sx={{ border: (thm) => `1px solid ${thm.palette.divider}` }}>
                      {t('event:useTicketsInfo')}
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      {fields.map((field, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Box sx={{ mb: 2 }} key={field.id}>
                          <GridWrapper narrow>
                            <Grid item xs={12} sm={6} md={2}>
                              <ControlledTextField
                                disabled={useTickets}
                                name={`participants.${index}.firstName`}
                                label={t('event:formFields.firstName')}
                                inputProps={{ maxLength: 50 }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                              <ControlledTextField
                                disabled={useTickets}
                                name={`participants.${index}.lastName`}
                                label={t('event:formFields.lastName')}
                                inputProps={{ maxLength: 50 }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.5}>
                              <ControlledTextField
                                disabled={useTickets}
                                name={`participants.${index}.email`}
                                label={t('event:formFields.email')}
                                inputProps={{ maxLength: 100 }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={1.5}>
                              <ControlledTextField
                                disabled={useTickets}
                                name={`participants.${index}.phone`}
                                label={t('event:formFields.phoneNumber')}
                                inputProps={{ maxLength: 50 }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                              <ControlledTextField
                                disabled={useTickets}
                                name={`participants.${index}.allergy`}
                                label={t('event:formFields.allergies')}
                                inputProps={{ maxLength: 50 }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={2} md={1}>
                              <ControlledTextField
                                disabled={useTickets}
                                name={`participants.${index}.fee`}
                                label={t('event:fee')}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} sm={2} md={1} justifyContent={{ sm: 'flex-end' }}>
                              <Button disabled={useTickets} variant='contained' onClick={() => remove(index)} disableElevation>
                                {t('common:button.delete')}
                              </Button>
                            </Grid>
                          </GridWrapper>
                        </Box>
                      ))}
                      <GridWrapper>
                        <Grid item xs={12}>
                          <Button
                            disabled={useTickets}
                            variant='outlined'
                            startIcon={<AddCircle />}
                            onClick={() =>
                              append({
                                firstName: '',
                                lastName: '',
                                email: '',
                                member_id: 0,
                                event_id: eventId,
                                fee: eventFee || 0,
                                allergy: '',
                                phone: '',
                              })
                            }
                          >
                            {t('event:addParticipants')}
                          </Button>
                        </Grid>
                      </GridWrapper>
                    </form>
                  </FormProvider>
                </Grid>
              </GridWrapper>
            </Box>
          </Box>
        }
        dialogActions={
          <Box display='flex' flexDirection='row' justifyContent='space-between' width='100%'>
            <FormControlLabel
              sx={{ ml: 0.5 }}
              disabled={useTickets}
              control={
                <Checkbox
                  onChange={(e) => {
                    setCreateInvoiceValue(e.target.checked);
                  }}
                  checked={createInvoiceValue}
                  value={createInvoiceValue}
                />
              }
              label={
                <Typography
                  sx={(theme) => ({
                    color: useTickets ? theme.palette.text.disabled : theme.palette.text.primary,
                  })}
                >
                  {t('event:createInvoiceToParticipants')}
                </Typography>
              }
            />
            <Button size='large' disabled={useTickets || loading} disableElevation variant='contained' color='primary' onClick={onHandleSave}>
              {t('common:button.save')}
            </Button>
          </Box>
        }
      />
      <Dialog open={confirmDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{t('event:confirmHasFee')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('event:confirmHasFeeMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color='primary'>
            {t('common:button.no')}
          </Button>
          <Button onClick={handleDialogConfirm} color='primary' variant='contained' disableElevation autoFocus>
            {t('common:button.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
